import { createContext, ReactNode, useContext, useEffect, useState } from 'react';
import { useAppSelector } from '../../../store/store';
import { useLazyGetSystemNotificationsQuery } from 'app/api/facade';
import { UserSystemNotification } from 'ais-facade-api/api';
import { useAlertHook } from 'app/hooks/useAlertHook';

type CustomUserSystemNotificationT = UserSystemNotification & { isOpen: boolean };

interface SystemNotificationContextType {
  messages: CustomUserSystemNotificationT[];
  updateOpenStatus: (key: string, isOpen: boolean) => void;
}

const SystemNotificationContext = createContext<SystemNotificationContextType | null>(null);
interface SystemNotificationProviderI {
  children: ReactNode;
}
export const SystemNotificationProvider = ({ children }: SystemNotificationProviderI) => {
  const [messages, setMessages] = useState<CustomUserSystemNotificationT[]>([]);
  const [getSystemNotifications, { data: systemNotificationsData }] = useLazyGetSystemNotificationsQuery();
  const auth = useAppSelector((state) => state.userSlice.auth);
  const { addAlert } = useAlertHook();
  const loadMessagesFromStorage = () => {
    const storedMessages = localStorage.getItem('systemNotifications');
    return storedMessages ? JSON.parse(storedMessages) : [];
  };

  const saveMessagesToStorage = (messages: CustomUserSystemNotificationT[]) => {
    localStorage.setItem('systemNotifications', JSON.stringify(messages));
  };

  const updateOpenStatus = (key: string, isOpen: boolean) => {
    const updatedMessages = messages.map((msg) => (msg.key === key ? { ...msg, isOpen } : msg));
    setMessages(updatedMessages);
    saveMessagesToStorage(updatedMessages);
  };

  useEffect(() => {
    if (auth?.value?.loggedIn) {
      const storedMessages = loadMessagesFromStorage();
      if (storedMessages.length === 0) {
        getSystemNotifications()
          .unwrap()
          .catch((error: Error) => addAlert(error));
      } else {
        setMessages(storedMessages);
      }
    }
  }, [auth, getSystemNotifications]);

  useEffect(() => {
    if (systemNotificationsData?.results) {
      const parsedSystemNotifications = systemNotificationsData.results.map((msg) => ({
        ...msg,
        isOpen: true,
      }));
      setMessages(parsedSystemNotifications);
      saveMessagesToStorage(parsedSystemNotifications);
    }
  }, [systemNotificationsData]);

  return (
    <SystemNotificationContext.Provider value={{ messages, updateOpenStatus }}>{children}</SystemNotificationContext.Provider>
  );
};

export const useSystemNotification = () => {
  const context = useContext(SystemNotificationContext);
  if (!context) {
    throw new Error('useSystemNotification must be used within a SystemNotificationProvider');
  }
  return context;
};
